import React from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import { FIELD_PLACEHOLDER_COLOR } from '../common/formFieldStyles';
import { BRAND, BRAND_RADIUS } from '../../theme/brandTokens';

const borderColor = BRAND.line;
const headerBackground = BRAND.background;
export const ALL_TEAM_MEMBERS_VALUE = '__all__';

const compactSelectSx = {
  height: 36,
  minWidth: { xs: 132, sm: 176 },
  maxWidth: { xs: 168, sm: 240 },
  bgcolor: BRAND.surface,
  fontSize: '0.8125rem',
  fontWeight: 600,
  borderRadius: BRAND_RADIUS.button,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor,
    borderWidth: '1px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: BRAND.accent,
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: BRAND.accent,
    borderWidth: '1px',
  },
  '& .MuiSelect-select': {
    py: 0.75,
    px: 1.25,
  },
};

export default function AgendaCalendarToolbar({
  t,
  isPhone,
  canReadAllAgendas,
  selectedTeamMemberIds,
  allTeamMembersSelected,
  selectedTeamMembers,
  teamMembers,
  onTeamMemberChange,
  onProfessionalSelectClose,
  isMultiTeamView,
  effectiveViewDays,
  onViewDaysChange,
  onToday,
  onNavigateRange,
  rangeLabel,
  ownTeamMemberName,
  selectedTeamMember,
  getTeamMemberPicture,
  getInitials,
  hideProfessionalFilter = false,
}) {
  const viewLabel = (longKey, shortKey) => (isPhone ? t(shortKey) : t(longKey));

  return (
    <Box
      sx={{
        px: { xs: 1.25, md: 2 },
        py: 1,
        borderBottom: `1px solid ${borderColor}`,
        bgcolor: headerBackground,
        flexShrink: 0,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: '1 1 auto' }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TodayIcon sx={{ fontSize: 16 }} />}
            onClick={onToday}
            sx={{
              borderColor: BRAND.accent,
              color: BRAND.accentText,
              whiteSpace: 'nowrap',
              minWidth: { xs: 'auto', sm: 64 },
              height: 36,
              px: { xs: 1, sm: 1.25 },
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              fontWeight: 700,
              '& .MuiButton-startIcon': {
                mr: { xs: 0.5, sm: 0.75 },
              },
            }}
          >
            {t('managementAppointments.today')}
          </Button>
          <Button
            variant="text"
            onClick={() => onNavigateRange(-1)}
            sx={{ minWidth: 32, px: 0.25, color: BRAND.text }}
            aria-label={t('common.back')}
          >
            <ChevronLeftIcon />
          </Button>
          <Typography
            variant="subtitle1"
            sx={{
              minWidth: { xs: 96, sm: 160 },
              maxWidth: { xs: 148, sm: 'none' },
              textAlign: 'center',
              fontWeight: 700,
              color: BRAND.text,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {rangeLabel}
          </Typography>
          <Button
            variant="text"
            onClick={() => onNavigateRange(1)}
            sx={{ minWidth: 32, px: 0.25, color: BRAND.text }}
            aria-label={t('common.next')}
          >
            <ChevronRightIcon />
          </Button>
          {!canReadAllAgendas && selectedTeamMember && (
            <Chip
              avatar={(
                <Avatar src={getTeamMemberPicture(selectedTeamMember) || undefined}>
                  {getInitials(selectedTeamMember.name)}
                </Avatar>
              )}
              label={selectedTeamMember.name || ownTeamMemberName}
              variant="outlined"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                bgcolor: BRAND.surface,
                borderColor,
                color: BRAND.text,
                fontWeight: 600,
                height: 36,
              }}
            />
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: '0 1 auto' }}>
          {canReadAllAgendas && !hideProfessionalFilter && (
            <Select
              multiple
              displayEmpty
              size="small"
              value={selectedTeamMemberIds}
              onChange={onTeamMemberChange}
              onClose={onProfessionalSelectClose}
              renderValue={() => {
                if (selectedTeamMemberIds.length === 0) {
                  return (
                    <Typography sx={{ color: FIELD_PLACEHOLDER_COLOR, fontSize: '0.8125rem' }}>
                      {t('managementAppointments.professional')}
                    </Typography>
                  );
                }
                if (allTeamMembersSelected) return t('common.all');
                return selectedTeamMembers.map((teamMember) => teamMember.name).join(', ');
              }}
              MenuProps={{ disableScrollLock: true }}
              sx={compactSelectSx}
            >
              <MenuItem value={ALL_TEAM_MEMBERS_VALUE}>
                <Checkbox checked={allTeamMembersSelected} />
                <ListItemText primary={t('common.all')} />
              </MenuItem>
              {teamMembers.map((teamMember) => (
                <MenuItem key={teamMember.id} value={teamMember.id}>
                  <Checkbox checked={selectedTeamMemberIds.includes(teamMember.id)} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar src={getTeamMemberPicture(teamMember) || undefined} sx={{ width: 24, height: 24 }}>
                      {getInitials(teamMember.name)}
                    </Avatar>
                    <ListItemText primary={teamMember.name} />
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          )}
          {!isMultiTeamView && (
            <ToggleButtonGroup
              size="small"
              exclusive
              value={effectiveViewDays}
              onChange={onViewDaysChange}
              color="primary"
              sx={{
                height: 36,
                '& .MuiToggleButton-root': {
                  px: { xs: 0.75, sm: 1.25 },
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <ToggleButton value={1}>{viewLabel('managementAppointments.views.oneDay', 'managementAppointments.views.oneDayShort')}</ToggleButton>
              <ToggleButton value={3}>{viewLabel('managementAppointments.views.threeDays', 'managementAppointments.views.threeDaysShort')}</ToggleButton>
              <ToggleButton value={7}>{viewLabel('managementAppointments.views.sevenDays', 'managementAppointments.views.sevenDaysShort')}</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
